/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import { useEffect, useRef, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import './MilestoneDetails.scss';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import PropTypes from 'prop-types';
import UseAxios from '../../../Api/http/http';
import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { MILESTONE_DETAILS } from '../../../Api/resources/endPoint';
import {
  getExportFileName,
  getExportFileNameWithDate,
} from '../../Delivery/utils';

/* -------------------------------------------------------------------------- */
/*  Base URL                                                                   */
/* -------------------------------------------------------------------------- */
const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${MILESTONE_DETAILS}`;
  }
  return `${baseUrl}:${servicePort}${serviceURL}/api${MILESTONE_DETAILS}`;
};

const baseURL = fetchBaseURL();

const fetchProjectDetailById = async (projectId, token) => {
  if (!projectId) return null;
  const response = await UseAxios({
    service: serviceName.PROJECT_MS,
    method: 'get',
    url: `${MILESTONE_DETAILS}/projects/${projectId}`,
    payload: {},
    headers: { token },
  });
  const project = response?.data?.data;
  return project && typeof project === 'object' ? project : null;
};

/* -------------------------------------------------------------------------- */
/*  Date formatting & column transform helpers                                 */
/* -------------------------------------------------------------------------- */
const AMOUNT_FIELD_REGEX = /amount|amt/i;

// Fields that must remain multi-select even when all others are forced to singleSelect
const MULTI_SELECT_FIELDS = ['cloud_provider', 'cloudProvider', 'cloud provider'];
const PROJECT_NAME_FIELDS = ['projectName', 'project_name'];

const formatDateValue = (value) => {
  if (!value || typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || trimmed === '-') return value;

  const slashMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
  if (slashMatch) {
    const parsed = dayjs(`${slashMatch[3]}-${slashMatch[2]}-${slashMatch[1]}`);
    return parsed.isValid() ? parsed.format('DD-MMM-YYYY') : value;
  }
  const parsed = dayjs(trimmed);
  return parsed.isValid() ? parsed.format('DD-MMM-YYYY') : value;
};

const applyDateFormatting = (rows = [], dateFields = []) => {
  if (dateFields.length === 0) return rows;
  return rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const updated = { ...row };
    dateFields.forEach((field) => {
      if (updated[field] != null) {
        updated[field] = formatDateValue(String(updated[field]));
      }
    });
    return updated;
  });
};

const matchesInitialProjectScope = (row, initialProjectId, initialProjectName) => {
  if (!row || typeof row !== 'object') return false;
  const rowProjectId = String(row?.projectId || '').trim();
  const rowProjectName = String(row?.projectName ?? row?.project_name ?? '').trim();
  const expectedProjectId = String(initialProjectId || '').trim();
  const expectedProjectName = String(initialProjectName || '').trim();

  if (expectedProjectId && rowProjectId === expectedProjectId) return true;
  if (expectedProjectName && rowProjectName === expectedProjectName) return true;
  return false;
};

const injectInitialProjectFilterSelection = ({
  response,
  selectedProject,
  initialProjectId,
  initialProjectName,
}) => {
  const tableConfiguration = response?.data?.tableConfiguration
    || response?.data?.data?.tableConfiguration
    || [];

  if (!Array.isArray(tableConfiguration) || !tableConfiguration.length) {
    return false;
  }

  const selectedProjectId = selectedProject?.projectId ?? initialProjectId;
  const selectedProjectName = selectedProject?.projectName
    ?? selectedProject?.project_name
    ?? initialProjectName;

  let didInject = false;

  const nextTableConfiguration = tableConfiguration.map((column) => {
    if (!PROJECT_NAME_FIELDS.includes(column?.field)) {
      return column;
    }

    return {
      ...column,
      menuItems: Array.isArray(column?.menuItems)
        ? column.menuItems.map((menuGroup) => ({
          ...menuGroup,
          items: Array.isArray(menuGroup?.items)
            ? menuGroup.items.map((item) => {
              if (item?.type !== 'filter') return item;

              const options = item?.data
                || item?.options
                || item?.filterOptions
                || item?.dropdownOptions
                || [];

              const resolvedOption = Array.isArray(options)
                ? options.find((option) => {
                  const optionValue = option?.value ?? option?.id ?? option;
                  const optionLabel = option?.label
                    ?? option?.name
                    ?? option?.projectName
                    ?? option?.project_name;

                  if (selectedProjectId && String(optionValue) === String(selectedProjectId)) return true;
                  if (
                    selectedProjectName
                    && String(optionLabel || '').trim() === String(selectedProjectName).trim()
                  ) {
                    return true;
                  }
                  return false;
                }) || null
                : null;

              const selectedValue = resolvedOption || selectedProjectId || null;
              const selectedLabel = resolvedOption?.label
                ?? resolvedOption?.name
                ?? resolvedOption?.projectName
                ?? resolvedOption?.project_name
                ?? selectedProjectName
                ?? '';

              didInject = true;

              return {
                ...item,
                selectedValue,
                selectedValues: selectedValue ? [selectedValue] : [],
                selectedLabel,
              };
            })
            : menuGroup?.items,
        }))
        : column?.menuItems,
    };
  });

  if (!didInject) {
    return false;
  }

  if (response?.data) {
    response.data.tableConfiguration = nextTableConfiguration;
    response.data.data = {
      ...(response.data.data || {}),
      tableConfiguration: nextTableConfiguration,
    };
  }

  return true;
};

/* -------------------------------------------------------------------------- */
/*  Filtered API response handler                                              */
/* -------------------------------------------------------------------------- */
const handleFilteredApiResponse = (response) => {
  if (!response) return;

  const normalizeStatus = (val) =>
    String(val || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  let rawTableConfiguration = [];
  if (Array.isArray(response.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.tableConfiguration;
  } else if (Array.isArray(response.data?.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.data.tableConfiguration;
  }

  // Ensure BLSD comes before BLED in the column order (Issue 5)
  const blsdIdx = rawTableConfiguration.findIndex((col) => col?.field === 'blsd');
  const bledIdx = rawTableConfiguration.findIndex((col) => col?.field === 'bled');
  if (blsdIdx !== -1 && bledIdx !== -1 && blsdIdx > bledIdx) {
    const tmp = rawTableConfiguration[blsdIdx];
    rawTableConfiguration[blsdIdx] = rawTableConfiguration[bledIdx];
    rawTableConfiguration[bledIdx] = tmp;
  }

  // Build style map from the 'status' column's style property (set by backend)
  const statusColumnConfig = rawTableConfiguration.find(
    (col) => col?.field === 'status' && col?.style && typeof col.style === 'object',
  );

  const statusStyleMap = Object.entries(statusColumnConfig?.style || {}).reduce(
    (acc, [key, val]) => {
      if (val && typeof val === 'object') {
        acc[normalizeStatus(key)] = { textColor: val.textColor, boxColor: val.boxColor };
      }
      return acc;
    },
    {},
  );

  // Apply status colors to each row's rowConfiguration.template
  const addStatusStyles = (rows = []) =>
    rows.map((row) => {
      if (!row || typeof row !== 'object') return row;
      const statusStyle = statusStyleMap[normalizeStatus(row.status)];
      const existingTemplate = row.rowConfiguration?.template || [];

      let nextTemplate = existingTemplate;
      if (statusStyle?.textColor || statusStyle?.boxColor) {
        const statusEntry = {
          field: 'status',
          ...(statusStyle.textColor ? { textColor: statusStyle.textColor } : {}),
          fontWeight: '600',
        };
        nextTemplate = [
          ...nextTemplate.filter((entry) => entry.field !== 'status'),
          statusEntry,
        ];
      }

      const projectNameField = PROJECT_NAME_FIELDS.find(
        (field) => Object.prototype.hasOwnProperty.call(row, field),
      );
      if (projectNameField) {
        nextTemplate = [
          ...nextTemplate.filter((entry) => entry.field !== projectNameField),
          { field: projectNameField, fontWeight: '700' },
        ];
      }

      if (nextTemplate === existingTemplate) return row;

      return {
        ...row,
        rowConfiguration: {
          ...(row.rowConfiguration || {}),
          template: nextTemplate,
        },
      };
    });

  const dateFields = rawTableConfiguration
    .filter((col) => col?.type === 'date' && col?.field)
    .map((col) => col.field);

  const rawTableData = response.data?.tableData || response.data?.data?.tableData || [];
  const tableData = applyDateFormatting(addStatusStyles(rawTableData), dateFields).map((row) => {
    if (!row || typeof row !== 'object') return row;
    const projectNameField = PROJECT_NAME_FIELDS.find(
      (field) => Object.prototype.hasOwnProperty.call(row, field),
    );
    return {
      ...row,
      rowConfiguration: {
        ...(row.rowConfiguration || {}),
        onClick: projectNameField ? [{ field: projectNameField }] : [],
      },
    };
  });

  response.data = {
    ...response.data,
    tableConfiguration: rawTableConfiguration.map((column) => {
      const fieldStr = String(column?.field || '');
      const isAmount = AMOUNT_FIELD_REGEX.test(fieldStr);
      const isFirstTwoCols = column?.order <= 1;

      const widthNum = Number(column?.width);
      const compactWidth = Number.isFinite(widthNum)
        ? Math.max(90, Math.round(widthNum * 0.88))
        : column?.width;

      return {
        ...column,
        width: compactWidth,
        defaultPinned: isFirstTwoCols,
        isPinned: isFirstTwoCols,
        pinnable: false,
        ...(isAmount ? { align: 'right', headerAlign: 'right' } : {}),
        menuItems: Array.isArray(column?.menuItems)
          ? column.menuItems.map((menuGroup) => ({
            ...menuGroup,
            items: Array.isArray(menuGroup?.items)
              ? menuGroup.items.map((item) => {
                if (
                  item?.type === 'filter'
                  && (item?.template === 'multiSelect' || item?.template === 'choice')
                  && !MULTI_SELECT_FIELDS.includes(item?.field)
                ) {
                  return {
                    ...item,
                    template: 'singleSelect',
                    selectedValues: [],
                    selectedValue: null,
                    selectedLabel: '',
                  };
                }
                return item;
              })
              : menuGroup?.items,
          }))
          : [],
      };
    }),
    tableData,
    data: {
      ...(response.data?.data || {}),
      tableData,
    },
  };

  const pagination
    = response.data?.pagination
    || response.data?.paginationConfig
    || response.data?.data?.pagination
    || response.data?.data?.paginationConfig;

  response.meta = {
    ...(response.meta || {}),
    page: pagination?.page ?? response.meta?.page ?? 1,
    limit: pagination?.limit ?? response.meta?.limit ?? 10,
    total:
      pagination?.total
      ?? pagination?.count
      ?? response.meta?.total
      ?? response.data?.tableData?.length
      ?? response.data?.data?.tableData?.length
      ?? 0,
  };
};

/* -------------------------------------------------------------------------- */
/*  DOM effects hook                                                           */
/* -------------------------------------------------------------------------- */
const useProjectListTableEffects = ({
  containerRef,
  setTableScrollHeight,
  onResetClick,
  getRowCount,
}) => {
  useEffect(() => {
    const rootNode = containerRef.current;

    const handleResetDetect = (event) => {
      if (!rootNode) return;
      const btn = event.target.closest('button');
      if (!btn || !rootNode.contains(btn)) return;

      const isReset = Boolean(event.target.closest('.pi-refresh'))
        || Boolean(btn.querySelector('.pi-refresh'));

      if (isReset) onResetClick();
    };

    rootNode?.addEventListener('click', handleResetDetect);

    const handleResize = () => {
      const rowCount = Number(getRowCount?.()) || 0;
      const contentHeight = Math.max(160, (rowCount * 34) + 92);
      const maxHeight = Math.floor(window.innerHeight * 0.42);
      setTableScrollHeight(`${Math.min(contentHeight, maxHeight)}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      rootNode?.removeEventListener('click', handleResetDetect);
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, setTableScrollHeight, onResetClick, getRowCount]);
};

/* -------------------------------------------------------------------------- */
/*  ProjectListTable component                                                  */
/* -------------------------------------------------------------------------- */
const ProjectListTable = ({
  onProjectSelect,
  onFilterApplied,
  onReset,
  noDataMode,
  toolBarHeading,
  initialProjectId,
  initialProjectName,
}) => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const initialLoadDone = useRef(false);
  const isResettingRef = useRef(false);
  const hasAutoSelectedInitialProjectRef = useRef(false);
  const hasInjectedInitialProjectFilterRef = useRef(false);

  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const [initialProjectRow, setInitialProjectRow] = useState(null);

  // NEW STATE: Tracks when the first successful data payload is received
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const latestRowCountRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    hasAutoSelectedInitialProjectRef.current = false;
    hasInjectedInitialProjectFilterRef.current = false;

    if (!initialProjectId) {
      setInitialProjectRow(null);
      return () => {
        isMounted = false;
      };
    }

    const loadInitialProject = async () => {
      const project = await fetchProjectDetailById(initialProjectId, userProfile.access_token);
      if (!isMounted) return;
      setInitialProjectRow(project?.projectId ? project : null);
    };

    loadInitialProject();

    return () => {
      isMounted = false;
    };
  }, [initialProjectId, userProfile.access_token]);

  const resetToFirstPage = useCallback(() => {
    isResettingRef.current = true;
    if (typeof onReset === 'function') onReset();
  }, [onReset]);

  const handleFilteredApiResponseWithState = useCallback((response) => {
    handleFilteredApiResponse(response);

    // Toggle state so the filter icon is now shown
    setIsDataLoaded(true);

    const responseRows = response.data?.tableData || response.data?.data?.tableData || [];

    const filteredRows = (
      initialProjectId || initialProjectName
    )
      ? responseRows.filter((row) => matchesInitialProjectScope(
        row,
        initialProjectId,
        initialProjectName,
      ))
      : responseRows;

    const rows = filteredRows.length
      ? filteredRows
      : (initialProjectRow?.projectId ? [initialProjectRow] : filteredRows);

    if (response.data) {
      response.data.tableData = rows;
      response.data.data = {
        ...(response.data.data || {}),
        tableData: rows,
      };
    }

    response.meta = {
      ...(response.meta || {}),
      total: rows.length,
    };

    latestRowCountRef.current = rows.length;

    const contentHeight = Math.max(160, (rows.length * 34) + 92);
    const maxHeight = Math.floor(window.innerHeight * 0.42);
    setTableScrollHeight(`${Math.min(contentHeight, maxHeight)}px`);

    if (
      !hasAutoSelectedInitialProjectRef.current
      && (initialProjectId || initialProjectName)
    ) {
      const matchedProject = rows.find((row) => matchesInitialProjectScope(
        row,
        initialProjectId,
        initialProjectName,
      )) || null;

      if (matchedProject?.projectId) {
        if (!hasInjectedInitialProjectFilterRef.current) {
          hasInjectedInitialProjectFilterRef.current = injectInitialProjectFilterSelection({
            response,
            selectedProject: matchedProject,
            initialProjectId,
            initialProjectName,
          });
        }
        hasAutoSelectedInitialProjectRef.current = true;
        onProjectSelect(matchedProject);
        initialLoadDone.current = true;
        return;
      }
    }

    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      return;
    }

    if (isResettingRef.current) {
      isResettingRef.current = false;
      return;
    }

    if (
      !hasInjectedInitialProjectFilterRef.current
      && (initialProjectId || initialProjectName)
    ) {
      hasInjectedInitialProjectFilterRef.current = injectInitialProjectFilterSelection({
        response,
        selectedProject: rows.find((row) => matchesInitialProjectScope(
          row,
          initialProjectId,
          initialProjectName,
        )) || null,
        initialProjectId,
        initialProjectName,
      });
    }

    if (typeof onFilterApplied === 'function') {
      const firstProject = rows.find((row) => row?.projectId) || null;
      onFilterApplied(firstProject);
    }
  }, [initialProjectId, initialProjectName, initialProjectRow, onFilterApplied, onProjectSelect]);

  const handleCellClick = useCallback(({ fieldPath, rowData }) => {
    const projectId = rowData?.projectId;
    if (!projectId) return;

    if (fieldPath === 'projectName' || fieldPath === 'project_name') {
      const projectDetailsUrl = `/delivery/project-details?projectId=${encodeURIComponent(String(projectId))}`;
      window.open(projectDetailsUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (typeof onProjectSelect === 'function') {
      onProjectSelect(rowData);
    }
  }, [onProjectSelect]);

  const getCurrentRowCount = useCallback(() => latestRowCountRef.current, []);

  useProjectListTableEffects({
    containerRef,
    setTableScrollHeight,
    onResetClick: resetToFirstPage,
    getRowCount: getCurrentRowCount,
  });

  const configuration = {
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: true },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('Milestone Details Projects'),
          sheetName: () => getExportFileNameWithDate('Milestone Details Projects'),
        },
      },
      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse: handleFilteredApiResponseWithState,
      },
      headers: {
        token: userProfile.access_token,
      },
      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: toolBarHeading || 'Milestone Details',
        showSaveView: false,
        showReset: true,
        showExpandCollapse: false,
        showFilterOption: isDataLoaded, // Conditionally render based on whether data has loaded
      },
      handlers: {
        handleResetIconClick: resetToFirstPage,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: 10,
      loaderSkeleton: {
        rows: 10,
        bodyCells: 2,
        columns: 6,
      },
      paginationConfig: undefined,
      showExpandCollapse: false,
      tableScrollHeight,
      maxFrozenColumns: {
        desktop: 2,
        tablet: 1,
        mobile: 1,
      },
    },
  };

  return (
    <div
      className={`ms-project-list-outer${noDataMode ? ' ms-no-data' : ''}`}
      ref={containerRef}
    >
      <div className="ms-project-list-table-utility">
        <TableUtility
          key={`${initialProjectId || initialProjectName || 'default'}-${initialProjectRow?.projectId || 'none'}`}
          configuration={configuration}
        />
      </div>
    </div>
  );
};

ProjectListTable.propTypes = {
  onProjectSelect: PropTypes.func.isRequired,
  onFilterApplied: PropTypes.func,
  onReset: PropTypes.func,
  noDataMode: PropTypes.bool,
  toolBarHeading: PropTypes.string,
  initialProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialProjectName: PropTypes.string,
};

ProjectListTable.defaultProps = {
  onFilterApplied: undefined,
  onReset: undefined,
  noDataMode: false,
  toolBarHeading: 'Milestone Details',
  initialProjectId: '',
  initialProjectName: '',
};

export default ProjectListTable;
